import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// Third-party
import { useContext } from 'react';
import { Trans } from 'react-i18next';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// Internal
import { fontSizes, spacing, statusColors } from 'syf-component-library/defaultTheme';
import { Button } from 'syf-component-library/ui/atoms';
import Collapse from 'syf-component-library/ui/atoms/Collapse';
import useWindowSize from 'syf-component-library/ui/customHooks/useWindowWidth';
import { Grid } from 'syf-component-library/ui/patterns';
import { Inline, Inset, Stack } from 'syf-component-library/ui/spacing';
import { Body } from 'syf-component-library/ui/typography';
import { DATA_ANALYTICS, DATA_TYPE, PAGE_FUNCTION, PAGE_NAME, SUB_FUNCTION } from '../../../../const/analytics';
import { FAR_ANGLE_DOWN, FAR_ANGLE_UP } from '../../../../const/iconProps';
import MainContext from '../../../../Context';
import { colors } from '../../../../defaultTheme';
import useAutopayBannerMachine from '../../../../hooks/machines/useAutopayBannerMachine';
import usePaymentFlowMachine from '../../../../hooks/machines/usePaymentFlowMachine';
import i18n, { t } from '../../../../strings/i18n';
import AutopayBannerCollapseButton from '../../../atoms/AutopayBannerCollapseButton';
import AutopayIcon from '../../../atoms/AutopayIcon';
import AutopayBannerStatusMessage from './AutopayBannerStatusMessage';
import CancelAutopayBanner from './CancelAutopayBanner';
const AUTOPAY_ENROLLED_STATE = 'enrollment.enrolled';
const MANAGE_AUTOPAY_ENROLLED_STATE = 'enrollment.enrolled.managing';
export const AutopayCollapseBanner = styled.div `
  background-color: ${colors.lightGrey};
  border-bottom: 1px solid ${colors.mediumGrey};
  position: relative;
`;
const CTAButton = styled(Button) `
  margin: 0;
  padding: 0;
  font-size: ${fontSizes.root}px;
`;
const AutopayCollapseBannerContent = styled(Grid) `
  position: relative;
`;
const CollapseWrapper = styled(Collapse) `
  position: relative;
  z-index: 1;
`;
const CollapseIcon = styled(FontAwesomeIcon) `
  position: absolute;
  right: ${spacing.medium}px;
  font-size: ${fontSizes.medium}px;
  color: ${colors.black};
`;
const CallToAction = () => {
    const { triggerAnalyticsEvent, sendToMainMachine } = useContext(MainContext);
    const [autopayBannerMachine, sendToAutopayBannerMachine] = useAutopayBannerMachine();
    const [, autopayActor] = usePaymentFlowMachine();
    const isAutopayEnrolled = autopayBannerMachine.matches(AUTOPAY_ENROLLED_STATE);
    const isManagingAutopay = autopayBannerMachine.matches(MANAGE_AUTOPAY_ENROLLED_STATE);
    const shouldDisplayCallToAction = !autopayActor.context.successType;
    const navigateToAutopayForm = () => {
        sendToMainMachine({
            type: 'SET_AUTOPAY_FLOW',
            isPaymentTypeVisible: false
        });
        triggerAnalyticsEvent({
            pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.AUTOPAY,
            pageName: PAGE_NAME.START
        });
    };
    const navigateToManageAutopay = () => {
        sendToMainMachine({
            type: 'SET_AUTOPAY_FLOW',
            isPaymentTypeVisible: false
        });
        triggerAnalyticsEvent({
            pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.AUTOPAY,
            pageSubFunction: SUB_FUNCTION.AUTOPAY.MANAGE_AUTOPAY,
            pageName: PAGE_NAME.VIEW
        });
    };
    const navigateToCancelAutopay = () => {
        sendToAutopayBannerMachine({
            type: 'PROMPT_UNENROLL_CONFIRMATION'
        });
    };
    if (isManagingAutopay) {
        return (_jsx(CTAButton, Object.assign({ buttonType: "text", "data-analytics": DATA_ANALYTICS.CLICK_TRACKING_EVENT, "data-object": "link", "data-reason": "turn autopay off banner", "data-type": DATA_TYPE.MAKE_A_PAYMENT, dataCy: "turn-autopay-off-button", onClick: navigateToCancelAutopay }, { children: t('button.turnAutopayOff') })));
    }
    if (isAutopayEnrolled) {
        return (_jsx(CTAButton, Object.assign({ buttonType: "text", "data-analytics": DATA_ANALYTICS.CLICK_TRACKING_EVENT, "data-cy": "manage-autopay-button", "data-object": "link", "data-reason": "manage autopay banner", "data-type": DATA_TYPE.MAKE_A_PAYMENT, onClick: navigateToManageAutopay }, { children: t('button.manageAutopay') })));
    }
    return (_jsx(_Fragment, { children: shouldDisplayCallToAction && (_jsx(CTAButton, Object.assign({ buttonType: "text", "data-analytics": DATA_ANALYTICS.CLICK_TRACKING_EVENT, "data-object": "link", "data-reason": "enroll in autopay banner", "data-type": DATA_TYPE.MAKE_A_PAYMENT, dataCy: "enroll-in-autopay-button", onClick: navigateToAutopayForm }, { children: t('button.enrollInAutopay') }))) }));
};
const AutopayBanner = () => {
    const [autopayBannerMachine, sendToAutopayBannerMachine] = useAutopayBannerMachine();
    const isExpandable = autopayBannerMachine.matches('accordion.enabled');
    const isExpanded = autopayBannerMachine.matches('accordion.enabled.expanded');
    const isUnenrolling = autopayBannerMachine.matches('enrollment.enrolled.unenrolling');
    const isAutopayEnrolled = autopayBannerMachine.matches(AUTOPAY_ENROLLED_STATE);
    const windowWidth = useWindowSize();
    const collapseIcon = isExpanded ? FAR_ANGLE_UP : FAR_ANGLE_DOWN;
    const toggleIsExpanded = () => {
        sendToAutopayBannerMachine({
            type: isExpanded ? 'COLLAPSE' : 'EXPAND'
        });
    };
    const shouldShowAutopayBanner = autopayBannerMachine.matches('visibility.visible');
    if (isUnenrolling) {
        return shouldShowAutopayBanner && _jsx(CancelAutopayBanner, {});
    }
    return (_jsx(_Fragment, { children: shouldShowAutopayBanner && (_jsx(AutopayCollapseBanner, { children: _jsx(Inset, Object.assign({ bottom: "xlarge", left: "large", right: "large", top: "medium" }, { children: _jsxs(AutopayCollapseBannerContent, Object.assign({ alignContent: "flex-start", wrap: "nowrap" }, { children: [!windowWidth.isLessThanTablet && (_jsxs(Grid, Object.assign({ alignContent: "flex-start", wrap: "nowrap" }, { children: [_jsx(AutopayIcon, { height: "48", width: "48" }), _jsx(Inline, { size: "large" })] }))), _jsxs(Grid, Object.assign({ item: true }, { children: [_jsxs(AutopayBannerCollapseButton, Object.assign({ isExpandable: isExpandable, isExpanded: isExpanded, toggleIsExpanded: toggleIsExpanded }, { children: [isExpandable && _jsx(CollapseIcon, { icon: collapseIcon }), _jsx(Body, Object.assign({ weight: "bold" }, { children: _jsx(Trans, { components: [
                                                    _jsx("span", { "data-cy": "autopay-status-text", style: {
                                                            textTransform: 'uppercase',
                                                            color: isAutopayEnrolled === true
                                                                ? statusColors.success.icon
                                                                : statusColors.error.icon
                                                        } })
                                                ], i18n: i18n, i18nKey: `autopayCollapseBanner.${isAutopayEnrolled ? 'autopayOn' : 'autopayOff'}` }) })), _jsx(Stack, { size: "small" }), _jsx(Body, Object.assign({ "data-cy": "autopay-banner-message", weight: "light" }, { children: _jsx(AutopayBannerStatusMessage, {}) }))] })), isExpandable && (_jsx("div", Object.assign({ hidden: !isExpanded }, { children: _jsxs(CollapseWrapper, Object.assign({ duration: 0, expand: isExpanded, id: "autopay-banner" }, { children: [_jsx(Stack, { respondToBreakpoints: ['small', 'medium'], size: "large" }), _jsx(CallToAction, {})] })) })))] }))] })) })) })) }));
};
export default AutopayBanner;
